<template>
	<div
		v-if="display"
		class="record-item"
		:class="visualState"
		:style="recordStyleComputed"
	>
		<div
			v-if="
				workbookHasPermission('record_editValues') &&
				!visualState['left-clip']
			"
			class="drag-grab left"
			@pointerdown="$emit('pointerDownLeft', $event)"
		>
			<div class="grab-bar two"></div>
			<div class="grab-bar one"></div>
		</div>
		<sheet-label-formatted
			:formatting="labelFormatting"
			class="label"
			:class="{
				'has-icon': visualState['record-has-children'] && widthPX > 48,
			}"
			@pointerdown="pointerDownLabel"
		>
			<div
				class="icon-left"
				v-if="visualState['record-has-children'] && widthPX > 48"
			>
				<r-standard-icon
					class="parent-icon"
					icon="folder"
					:size="{
						height: 12,
						width: 12,
					}"
					:hexColor="labelFormatting.textColor"
				></r-standard-icon>
			</div>
			<div
				class="label-inner"
				:class="{ untitled: visualState.untitled }"
			>
				{{ recordLabel }}
			</div>
			<div
				v-if="visualState['has-comments']"
				class="comment-triangle-container"
			>
				<div class="comment-triangle"></div>
			</div>
			<slot name="label"></slot>
		</sheet-label-formatted>
		<r-shadow
			v-if="
				visualState['has-simulated-values'] ||
				visualState['mouse-down-on-record']
			"
			:height="1"
			class="preview"
			:class="{
				right: visualState['date-preview-label-side'] === 'right',
			}"
		>
			{{ datePreviewText }}
		</r-shadow>
		<r-shadow
			v-if="
				!visualState['has-simulated-values'] &&
				!visualState['mouse-down-on-record'] &&
				visualState['show-record-label-preview']
			"
			:height="1"
			class="preview label"
		>
			{{ recordLabel }}
		</r-shadow>
		<r-shadow
			v-if="visualState['overlap-active']"
			:height="1"
			class="overlap-warning"
		>
			{{ recordLabel }} <br />
			This task overlaps another task
		</r-shadow>
		<div
			v-if="
				workbookHasPermission('record_editValues') &&
				!visualState['right-clip']
			"
			class="drag-grab right"
			@pointerdown="$emit('pointerDownRight', $event)"
		>
			<div class="grab-bar one"></div>
			<div class="grab-bar two"></div>
		</div>
	</div>
</template>
<script>
import SheetLabelFormatted from 'vue/sheet-views/labels/sheet-label-formatted.vue';
export default {
	inject: ['workbookHasPermission'],
	props: {
		display: {
			required: false,
			type: Boolean,
			default: true,
		},
		visualState: {
			required: true,
			type: Object,
		},
		widthPX: {
			required: true,
			type: Number,
		},
		offsetX: {
			required: true,
			type: Number,
		},
		offsetY: {
			required: true,
			type: Number,
		},
		recordLabel: {
			required: true,
			type: String,
		},
		labelFormatting: {
			required: true,
			type: Object,
		},
		datePreviewText: {
			required: true,
			type: String,
		},
	},
	components: {
		SheetLabelFormatted,
	},
	computed: {
		recordStyleComputed() {
			return {
				width: `${this.widthPX}px`,
				transform: `translate(${this.offsetX}px, ${this.offsetY}px)`,
			};
		},
	},
	methods: {
		pointerDownLabel(event) {
			this.$emit('pointerDownLabel', event);
		},
	},
};
</script>
<style lang="scss" scoped>
.record-item {
	user-select: none;
	position: absolute;
	height: 32px;
	line-height: 32px;
	margin-top: 4px;
	box-sizing: border-box;
	border: 1px solid #aeb4be;
	border-radius: 3px;
	background: rgba(255, 255, 255, 0.7);
	cursor: default;
	// -webkit-mask-image: linear-gradient(90deg, black, rgba(0,0,0,0.0));
	// mask-image:  linear-gradient(90deg, black, rgba(0,0,0,0.5));
	&:hover {
		z-index: 1;
	}
	&.drag-enabled {
		cursor: move;
	}
	&.selected {
		border: 2px solid $p-color-grey-600;
		z-index: 2;
	}
	&.comment-pop-open {
		z-index: 50 !important;
		border: 2px solid $p-color-orange-300;
	}
	&.mouse-down-on-record,
	&.resizing {
		border: 2px solid $p-color-grey-600;
		z-index: 5;
	}
	&.overlaps {
		background: rgba(255, 255, 255, 0.7);
	}
	&.overlap-active {
		border: 2px dashed $p-color-orange-300;
	}
	&.cut-right {
		border-right: none;
		border-radius: 3px 0 0 3px;
	}
	&.cut-left {
		border-left: none;
		border-radius: 0 3px 3px 0;
	}
	&.cut-right.cut-left {
		border-radius: 0;
	}
	&.tiny {
		//When it's just one
		.label {
			// padding: 0;
			.label-inner {
				text-overflow: clip;
			}
			&.overlap-active {
				grid-template-columns: 0 100%;
			}
		}
	}
	&.ghost {
		opacity: 0.4;
		transition: opacity $p-transition-slow;
	}
	&.ghost-right {
		-webkit-mask-image: linear-gradient(
			to right,
			rgba(0, 0, 0, 1),
			rgba(0, 0, 0, 0.4)
		);
		-webkit-mask-size: 100% 100%;
		-webkit-mask-repeat: no-repeat;
		-webkit-mask-position:
			left top,
			left bottom;
	}
	&.ghost-left {
		-webkit-mask-image: linear-gradient(
			to left,
			rgba(0, 0, 0, 1),
			rgba(0, 0, 0, 0.4)
		);
		-webkit-mask-size: 100% 100%;
		-webkit-mask-repeat: no-repeat;
		-webkit-mask-position:
			left top,
			left bottom;
	}
	.gradient {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background-image: linear-gradient(
			90deg,
			rgba(255, 255, 255, 0),
			rgba(255, 255, 255, 0.5)
		);
	}
}
.drag-grab {
	position: absolute;
	top: 0;
	height: 100%;
	width: 8px;
	display: flex;
	align-items: center;
	z-index: 1;
	&.left {
		left: 0;
		cursor: w-resize;
	}
	&.right {
		right: 0;
		cursor: e-resize;
	}
	.grab-bar {
		background: $p-color-grey-600;
		width: 2px;
		margin: 0 1px;
		height: 20px;
		opacity: 0;
		transition: opacity $p-transition-fast;
		&.one {
			width: 1px;
		}
		&.two {
			width: 1px;
		}
	}
}
.record-item:hover .grab-bar,
.record-item.resizing .grab-bar,
.drag-grab:hover .grab-bar {
	opacity: 1;
}
.label {
	display: grid;
	grid-template-columns: 100%;
	user-select: none;
	padding: 0 9px;
	height: 100%;
	border-radius: 3px;
	&.has-icon {
		grid-template-columns: 27px 1fr;
	}
	.icon-left {
		width: 21px;
		height: 30px;
		user-select: none;
		.parent-icon {
			position: relative;
			top: 1px;
		}
	}
	.label-inner {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		&.untitled {
			color: #a0a5ae;
		}
	}
	.overlap-icon {
		padding-top: 2px;
		display: flex;
		align-items: center;
		justify-content: center;
	}
	.comment-triangle-container {
		position: absolute;
		top: 0;
		left: 0;
		border-top: 10px solid $p-color-white;
		border-right: 10px solid transparent;
		.comment-triangle {
			position: absolute;
			top: -10px;
			left: 0px;
			border-top: 9px solid $p-color-purple-400;
			border-right: 9px solid transparent;
		}
	}
}
.left-clip {
	.label {
		border-radius: 0 3px 3px 0;
	}
}
.right-clip {
	.label {
		border-radius: 3px 0 0 3px;
	}
}
.overlap-warning {
	position: absolute;
	// height: 24px;
	background: #2c2c38;
	color: white;
	top: 36px;
	left: -2px;
	width: max-content;
	font-size: 14px;
	letter-spacing: 0.3px;
	border-radius: 3px;
	line-height: 24px;
	padding-left: 9px;
	padding-right: 9px;
	opacity: 0;
	pointer-events: none;
}
.record-item.overlaps:hover .overlap-warning {
	opacity: 1;
}
.record-item.overlaps.resizing:hover .overlap-warning {
	opacity: 0;
}
.preview {
	position: absolute;
	height: 24px;
	background: #2c2c38;
	color: white;
	bottom: -30px;
	left: -2px;
	width: max-content;
	font-size: 14px;
	letter-spacing: 0.3px;
	border-radius: 3px;
	line-height: 24px;
	padding-left: 9px;
	padding-right: 9px;
	z-index: 5;
	&.right {
		left: initial;
		right: -2px;
	}
	&.label {
		pointer-events: none;
		opacity: 0;
	}
}
.record-item:hover .preview.label {
	opacity: 1;
}
</style>
